import { cn } from "../utils/cn";

export type ButtonIntent = "default" | "primary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonState = "default" | "hover" | "active" | "focus" | "disabled" | "loading";

type ButtonProps = {
  label: string;
  intent?: ButtonIntent;
  size?: ButtonSize;
  state?: ButtonState;
  onClick?: () => void;
};

const base =
  "inline-flex items-center justify-center rounded-md font-semibold transition duration-150 ease-out hover:scale-[1.02]";
const intents: Record<ButtonIntent, string> = {
  default: "bg-surface text-text border border-border",
  primary: "bg-primary text-primary-foreground border border-primary",
  danger: "bg-danger text-primary-foreground border border-danger",
  ghost: "bg-transparent text-text border border-transparent",
};
const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-5 text-sm",
};

const stateClasses: Record<ButtonState, string> = {
  default: "",
  hover: "",
  active: "",
  focus: "outline outline-2 outline-primary/40 outline-offset-2",
  disabled: "opacity-50 pointer-events-none",
  loading: "opacity-70",
};

const hoverByIntent: Record<ButtonIntent, string> = {
  default: "bg-surface-muted",
  primary: "bg-primary/90",
  danger: "bg-danger/90",
  ghost: "bg-surface-muted",
};

const activeByIntent: Record<ButtonIntent, string> = {
  default: "bg-border/40",
  primary: "bg-primary/80",
  danger: "bg-danger/80",
  ghost: "bg-border/40",
};

export function Button({ label, intent = "default", size = "md", state = "default", onClick }: ButtonProps) {
  const intentState =
    state === "hover" ? hoverByIntent[intent] : state === "active" ? activeByIntent[intent] : stateClasses[state];
  const isDisabled = state === "disabled" || state === "loading";
  return (
    <button
      className={cn(base, intents[intent], sizes[size], intentState)}
      type="button"
      onClick={onClick}
      disabled={isDisabled}
    >
      {state === "loading" ? "Chargement…" : label}
    </button>
  );
}
